import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn } from "lucide-react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { PuzzleMark } from "@/components/puzzle/PuzzleMark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputStyle = {
  background: "transparent",
  border: "none",
  borderBottom: "1px solid rgba(192,192,192,0.3)",
  color: "#F5F1E8",
  fontFamily: "Montserrat",
  fontSize: 16,
  padding: "12px 0",
  height: "auto",
  borderRadius: 0,
};

const labelStyle = {
  color: "#B89B5E",
  fontFamily: "Montserrat",
  fontSize: 10,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  fontWeight: 600,
  marginBottom: 8,
  display: "block",
};

const AdminLogin = () => {
  const { status, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "authed") return <Navigate to="/admin/leads" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) navigate("/admin/leads", { replace: true });
    else setError(res.error || "Login failed.");
  };

  return (
    <main
      data-testid="admin-login-page"
      style={{
        background: "#0A0A0A",
        color: "#F5F1E8",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={handleSubmit}
        data-testid="admin-login-form"
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#0F0F0F",
          border: "1px solid rgba(192,192,192,0.15)",
          padding: "40px 32px 36px",
          position: "relative",
        }}
        noValidate
      >
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 48,
            height: 3,
            background: "#B89B5E",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
          <PuzzleMark size={36} animated={false} />
          <div style={{ lineHeight: 1.15 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: "0.18em",
                color: "#F5F1E8",
              }}
            >
              KRISTER OVERSEAS
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.24em",
                color: "#B89B5E",
                textTransform: "uppercase",
                marginTop: 4,
              }}
            >
              Admin Console
            </div>
          </div>
        </div>

        <h1
          style={{
            fontFamily: "Montserrat",
            fontWeight: 800,
            fontSize: 28,
            letterSpacing: "-0.015em",
            lineHeight: 1.05,
            margin: 0,
            textTransform: "uppercase",
          }}
        >
          Sign in.
        </h1>
        <p style={{ color: "#C0C0C0", fontSize: 13, marginTop: 8, marginBottom: 28 }}>
          Restricted to authorised Krister Overseas staff.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <Label htmlFor="al-email" style={labelStyle}>
              Email
            </Label>
            <Input
              id="al-email"
              data-testid="al-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@kristeroverseas.com"
              autoComplete="username"
              style={inputStyle}
              required
            />
          </div>
          <div>
            <Label htmlFor="al-password" style={labelStyle}>
              Password
            </Label>
            <Input
              id="al-password"
              data-testid="al-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              style={inputStyle}
              required
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="al-error"
            style={{
              marginTop: 22,
              padding: "10px 12px",
              background: "rgba(214,0,127,0.08)",
              border: "1px solid rgba(214,0,127,0.4)",
              color: "#F5F1E8",
              fontSize: 12,
              letterSpacing: "0.04em",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="al-submit"
          disabled={submitting}
          style={{
            marginTop: 32,
            width: "100%",
            background: "#F5F1E8",
            color: "#0A0A0A",
            fontFamily: "Montserrat",
            fontWeight: 700,
            letterSpacing: "0.24em",
            fontSize: 11,
            textTransform: "uppercase",
            padding: "16px 18px",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.7 : 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {submitting ? "Signing in…" : "Sign In"}
          <LogIn size={14} strokeWidth={1.5} />
        </button>

        <div
          style={{
            marginTop: 28,
            color: "#C0C0C0",
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          ← <a href="/" style={{ color: "#C0C0C0" }}>Return to website</a>
        </div>
      </motion.form>
    </main>
  );
};

export default AdminLogin;
